import { ApiResponseType, jsonInit, toSearchParams } from '@shigo-ui/api-client';
import { z } from 'zod';

import { createShigoApiClient, type ShigoApiConfig } from '../../../lib/shigo-api-client';
import {
  adsTxtAppsPageSchema,
  adsTxtDomainCountsSchema,
  adsTxtDomainsPageSchema,
  adsTxtMutationResponseSchema,
  adsTxtRecordsPageSchema,
  adsTxtReferencePageSchema,
  AdsTxtEntityStatus,
  AdsTxtRecordType,
  type AdsTxtAppsFilter,
  type AdsTxtDomainsFilter,
  type AdsTxtRecordPayload,
  type AdsTxtRecordsFilter,
} from './model';

interface AdsTxtClientOptions {
  config: ShigoApiConfig;
  accessToken: string;
  fetcher?: typeof fetch;
}

export function createAdsTxtClient({ config, accessToken, fetcher }: AdsTxtClientOptions) {
  const api = createShigoApiClient(config, accessToken, fetcher);
  return {
    listRecords: (filter: AdsTxtRecordsFilter) =>
      api.request({
        path: `/api/adstxts?${serializeAdsTxtRecordsFilter(filter)}`,
        schema: adsTxtRecordsPageSchema,
      }),
    exportRecords: (filter: AdsTxtRecordsFilter) =>
      api.request({
        path: `/api/adstxts/csv/export?${serializeAdsTxtRecordsFilter(filter)}`,
        schema: z.string(),
        responseType: ApiResponseType.Text,
      }),
    createRecord: (payload: AdsTxtRecordPayload) =>
      api.request({
        path: '/api/adstxts',
        schema: adsTxtMutationResponseSchema,
        init: jsonInit('POST', payload),
      }),
    updateRecord: (id: string, payload: AdsTxtRecordPayload) =>
      api.request({
        path: `/api/adstxts/${encodeURIComponent(id)}`,
        schema: adsTxtMutationResponseSchema,
        init: jsonInit('PUT', payload),
      }),
    deleteRecord: (id: string) =>
      api.request({
        path: `/api/adstxts/${encodeURIComponent(id)}`,
        schema: adsTxtMutationResponseSchema,
        init: { method: 'DELETE' },
      }),
    listApps: (filter: AdsTxtAppsFilter) =>
      api.request({
        path: `/api/apps?${serializeAdsTxtAppsFilter(filter)}`,
        schema: adsTxtAppsPageSchema,
      }),
    crawlApp: (id: string) =>
      api.request({
        path: `/api/apps/${encodeURIComponent(id)}/adstxt-crawl`,
        schema: adsTxtMutationResponseSchema,
      }),
    listDomains: (filter: AdsTxtDomainsFilter) =>
      api.request({
        path: `/api/publisher-domains?${serializeAdsTxtDomainsFilter(filter)}`,
        schema: adsTxtDomainsPageSchema,
      }),
    countDomains: () =>
      api.request({
        path: '/api/publisher-domains/adstxt-statuses',
        schema: adsTxtDomainCountsSchema,
      }),
    crawlDomain: (publisherId: string, domainId: string) =>
      api.request({
        path: `/api/publishers/${encodeURIComponent(publisherId)}/domains/${encodeURIComponent(domainId)}/adstxt-crawl`,
        schema: adsTxtMutationResponseSchema,
      }),
    listPublishers: () =>
      api.request({
        path: `/api/publishers?${toSearchParams({ page: 0, size: 1000, status: AdsTxtEntityStatus.Active, sortProperty: 'name', sortDirection: 'ASC' })}`,
        schema: adsTxtReferencePageSchema,
      }),
    listDemandPartners: () =>
      api.request({
        path: `/api/dps?${toSearchParams({ page: 0, size: 1000, status: AdsTxtEntityStatus.Active, sortProperty: 'displayName', sortDirection: 'ASC' })}`,
        schema: adsTxtReferencePageSchema,
      }),
  };
}

export function serializeAdsTxtRecordsFilter(filter: AdsTxtRecordsFilter) {
  return toSearchParams({
    page: filter.page,
    size: filter.size,
    searchTerm: filter.searchTerm.trim() || undefined,
    web: filter.types.includes(AdsTxtRecordType.Web) || undefined,
    ctv: filter.types.includes(AdsTxtRecordType.Ctv) || undefined,
    full: filter.types.length === 0 || undefined,
  }).toString();
}

export function serializeAdsTxtAppsFilter(filter: AdsTxtAppsFilter) {
  return toSearchParams({
    page: filter.page,
    size: filter.size,
    searchTerm: filter.searchTerm.trim() || undefined,
    publisherIds: filter.publisherIds.length ? filter.publisherIds : undefined,
    adstxtFoundCriteria: filter.foundCriteria,
  }).toString();
}

export function serializeAdsTxtDomainsFilter(filter: AdsTxtDomainsFilter) {
  return toSearchParams({
    page: filter.page,
    size: filter.size,
    searchTerm: filter.searchTerm.trim() || undefined,
    adstxtStatuses: filter.statuses.length ? filter.statuses : undefined,
  }).toString();
}
